'use client';

import { useCallback, useEffect, useRef } from 'react';

// Preloader constants
const RETRY_BASE_DELAY = 120; // Base delay for retry in ms
const RETRY_INCREMENTAL_DELAY = 180; // Additional delay per retry attempt
const AUTO_START_DELAY = 10; // Delay before auto-starting load

export type Phase = 1 | 2 | 'done';

export type PhasedStats = {
  cached: number;

  phase: Phase;

  phase1Total: number;
  phase1Loaded: number;

  phase2Total: number;
  phase2Loaded: number;

  timeouts: number;
  retries: number;

  inFlight: number;
  peakInFlight: number;
};

type LoadImage = {
  img: HTMLImageElement | null;
  loaded: boolean;
  loading: boolean;
  timedOut: boolean;
  retries: number;
};

type Strategy = 'even' | 'first';

export type UseFramePreloaderOptions = {
  total: number;
  getFileSrc: (i: number) => string;

  loadParallel?: number;
  phase1Count?: number;
  phase1Strategy?: Strategy;

  timeoutMs?: number;
  maxRetries?: number;

  autoStart?: boolean;
};

/** Clamps a number to integer within min/max bounds */
function clampInt(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n | 0));
}

/** Returns array with unique values, preserving order */
function unique(list: number[]) {
  const s = new Set<number>();
  const out: number[] = [];
  for (const v of list) {
    if (!s.has(v)) {
      s.add(v);
      out.push(v);
    }
  }
  return out;
}

/**
 * Builds indices for phase 1 loading based on strategy
 * @param total - Total number of frames
 * @param count - Number of frames to load in phase 1
 * @param strategy - 'first' loads sequentially, 'even' distributes evenly
 */
function buildPhase1Indices(total: number, count: number, strategy: Strategy) {
  const c = Math.max(0, Math.min(total, count));

  if (strategy === 'first') {
    return Array.from({ length: c }, (_, i) => i);
  }

  // Even distribution strategy
  if (c === 0) return [];
  if (c === 1) return [0];

  const out: number[] = [];
  for (let k = 0; k < c; k++) {
    const t = k / (c - 1);
    out.push(Math.round(t * (total - 1)));
  }
  return unique(out);
}

export function useFramePreloader({
  total,
  getFileSrc,

  loadParallel = 12,
  phase1Count = 700,
  phase1Strategy = 'even',

  timeoutMs = 4000,
  maxRetries = 1,

  autoStart = true,
}: UseFramePreloaderOptions) {
  const listRef = useRef<LoadImage[]>(
    Array.from({ length: total }, () => ({
      img: typeof Image !== 'undefined' ? new Image() : null,
      loaded: false,
      loading: false,
      timedOut: false,
      retries: 0,
    }))
  );

  const loadedFlagsRef = useRef<Uint8Array>(new Uint8Array(total));

  const phaseRef = useRef<Phase>(1);
  const phase1SetRef = useRef<Set<number>>(new Set());
  const phase2SetRef = useRef<Set<number>>(new Set());

  const timeoutsRef = useRef(0);
  const retriesRef = useRef(0);
  const startedRef = useRef(false);

  const inFlightRef = useRef(0);
  const peakInFlightRef = useRef(0);

  /** Returns optimized source URL for given frame index */
  const getOptimizedSrc = useCallback(
    (i: number) => getFileSrc(i),
    [getFileSrc]
  );

  /** Returns array of loaded frame flags (1 = loaded, 0 = not loaded) */
  const getLoadedFlags = useCallback(() => loadedFlagsRef.current, []);

  /**
   * Initializes preloader data structures and phase sets
   * Prepares Image objects and splits frames into two loading phases
   */
  const prime = useCallback(() => {
    if (listRef.current.length !== total) {
      listRef.current = Array.from({ length: total }, () => ({
        img: typeof Image !== 'undefined' ? new Image() : null,
        loaded: false,
        loading: false,
        timedOut: false,
        retries: 0,
      }));
    }

    if (loadedFlagsRef.current.length !== total) {
      loadedFlagsRef.current = new Uint8Array(total);
    } else {
      loadedFlagsRef.current.fill(0);
    }

    for (let i = 0; i < total; i++) {
      const rec = listRef.current[i];
      if (!rec.img) rec.img = new Image();
      loadedFlagsRef.current[i] = rec.loaded ? 1 : 0;
    }

    const p1 = buildPhase1Indices(total, phase1Count, phase1Strategy);
    phase1SetRef.current = new Set(p1);

    const p2: number[] = [];
    for (let i = 0; i < total; i++) {
      if (!phase1SetRef.current.has(i)) p2.push(i);
    }
    phase2SetRef.current = new Set(p2);
  }, [total, phase1Count, phase1Strategy]);

  /**
   * Loads a single frame image with timeout and retry logic
   * Tracks in-flight requests and handles failures gracefully
   */
  const loadOne = useCallback(
    (index: number) => {
      return new Promise<void>((resolve) => {
        if (index < 0 || index >= total) return resolve();

        const rec = listRef.current[index];
        if (!rec?.img) return resolve();
        if (rec.loaded || rec.loading) return resolve();

        rec.loading = true;

        inFlightRef.current += 1;
        peakInFlightRef.current = Math.max(
          peakInFlightRef.current,
          inFlightRef.current
        );

        const img = rec.img;
        let done = false;

        const finish = () => {
          if (done) return;
          done = true;
          rec.loading = false;
          inFlightRef.current = Math.max(0, inFlightRef.current - 1);
          resolve();
        };

        const retryLater = () => {
          const delay =
            RETRY_BASE_DELAY + rec.retries * RETRY_INCREMENTAL_DELAY;
          window.setTimeout(() => {
            void loadOne(index).then(finish);
          }, delay);
        };

        const timer = window.setTimeout(() => {
          if (!rec.loaded) {
            rec.timedOut = true;
            timeoutsRef.current += 1;

            if (rec.retries < maxRetries) {
              rec.retries += 1;
              retriesRef.current += 1;

              rec.loading = false;
              inFlightRef.current = Math.max(0, inFlightRef.current - 1);

              retryLater();
              return;
            }
          }
          finish();
        }, timeoutMs);

        img.onload = () => {
          rec.loaded = true;
          loadedFlagsRef.current[index] = 1;
          rec.timedOut = false;
          window.clearTimeout(timer);
          finish();
        };

        img.onerror = () => {
          window.clearTimeout(timer);

          if (rec.retries < maxRetries) {
            rec.retries += 1;
            retriesRef.current += 1;

            rec.loading = false;
            inFlightRef.current = Math.max(0, inFlightRef.current - 1);

            retryLater();
            return;
          }
          finish();
        };

        img.decoding = 'async';
        img.src = getOptimizedSrc(index);
      });
    },
    [getOptimizedSrc, maxRetries, timeoutMs, total]
  );

  /**
   * Loads multiple frames in parallel with concurrency control
   * Uses worker pool pattern to limit simultaneous requests
   */
  const loadMany = useCallback(
    async (indices: number[]) => {
      const list = unique(indices).filter((i) => i >= 0 && i < total);
      if (!list.length) return;

      let cursor = 0;
      const workersCount = Math.max(1, Math.min(loadParallel, list.length));

      /** Worker processes frames sequentially from shared cursor */
      const worker = async () => {
        while (cursor < list.length) {
          const i = list[cursor++];
          await loadOne(i);
        }
      };

      await Promise.allSettled(
        Array.from({ length: workersCount }, () => worker())
      );
    },
    [loadOne, loadParallel, total]
  );

  /** Starts phase 1 loading (priority frames) */
  const startPhase1 = useCallback(async () => {
    prime();
    phaseRef.current = 1;

    const p1 = Array.from(phase1SetRef.current.values());
    const seeds = unique([0, total - 1, ...p1]);

    await loadMany(seeds);
  }, [loadMany, prime, total]);

  /** Starts phase 2 loading (remaining frames) */
  const startPhase2 = useCallback(async () => {
    phaseRef.current = 2;

    const p2 = Array.from(phase2SetRef.current.values());
    await loadMany(p2);

    phaseRef.current = 'done';
  }, [loadMany]);

  /** Starts the complete loading process (both phases) */
  const start = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;

    await startPhase1();
    await startPhase2();
  }, [startPhase1, startPhase2]);

  /**
   * Hints to preload frames around a center point within radius
   * Useful for preloading frames near the current scroll position
   */
  const hintAround = useCallback(
    (center: number, radius = 160) => {
      if (phaseRef.current === 'done') return;

      const c = clampInt(center, 0, total - 1);
      const r = Math.max(0, radius | 0);

      const indices: number[] = [];
      for (let i = c - r; i <= c + r; i++) {
        if (i < 0 || i >= total) continue;
        const rec = listRef.current[i];
        if (rec && !rec.loaded && !rec.loading) indices.push(i);
      }

      if (indices.length) void loadMany(indices);
    },
    [loadMany, total]
  );

  /** Returns cached image element for given index if loaded */
  const getCached = useCallback(
    (i: number) => {
      const idx = clampInt(i, 0, total - 1);
      const rec = listRef.current[idx];
      if (rec?.loaded && rec.img) return rec.img;
      return undefined;
    },
    [total]
  );

  /** Returns current loading statistics and progress */
  const stats = useCallback((): PhasedStats => {
    let loadedP1 = 0;
    let loadedP2 = 0;

    for (const i of phase1SetRef.current)
      if (listRef.current[i]?.loaded) loadedP1++;

    for (const i of phase2SetRef.current)
      if (listRef.current[i]?.loaded) loadedP2++;

    return {
      cached: loadedP1 + loadedP2,

      phase: phaseRef.current,

      phase1Total: phase1SetRef.current.size,
      phase1Loaded: loadedP1,

      phase2Total: phase2SetRef.current.size,
      phase2Loaded: loadedP2,

      timeouts: timeoutsRef.current,
      retries: retriesRef.current,

      inFlight: inFlightRef.current,
      peakInFlight: peakInFlightRef.current,
    };
  }, []);

  useEffect(() => {
    if (!autoStart) return;
    const id = window.setTimeout(() => void start(), AUTO_START_DELAY);
    return () => window.clearTimeout(id);
  }, [autoStart, start]);

  useEffect(() => {
    return () => {
      startedRef.current = false;
      timeoutsRef.current = 0;
      retriesRef.current = 0;
      phaseRef.current = 1;
      inFlightRef.current = 0;
      peakInFlightRef.current = 0;
    };
  }, []);

  return {
    prime,
    start,
    startPhase1,
    startPhase2,

    hintAround,
    getCached,
    getLoadedFlags,
    getOptimizedSrc,
    stats,
  };
}
